import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Camera, RotateCcw, X, AlertTriangle, Clock, Trash2 } from "lucide-react";

/**
 * iter55 — Draft Snapshots modal (site-admin only).
 *
 * Lists snapshots for a single tournament. Per-row Restore button opens an
 * inline "type RESTORE to confirm" gate (defense-in-depth — restore is
 * destructive). Manual capture button forces a new snapshot on demand.
 */
export default function SiteAdminDraftSnapshots({ tournamentId, tournamentName, onClose }) {
    const [loading, setLoading] = useState(true);
    const [snapshots, setSnapshots] = useState([]);
    const [capturing, setCapturing] = useState(false);
    const [restoreFor, setRestoreFor] = useState(null); // snapshot row pending restore
    const [confirmText, setConfirmText] = useState("");
    const [restoring, setRestoring] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.siteListDraftSnapshots(tournamentId);
            setSnapshots(r.snapshots || []);
        } catch (e) {
            toast.error("Failed to load snapshots");
        } finally {
            setLoading(false);
        }
    }, [tournamentId]);

    useEffect(() => { load(); }, [load]);

    const handleManualCapture = async () => {
        setCapturing(true);
        try {
            const r = await api.siteCaptureDraftSnapshot(tournamentId);
            toast.success(`Snapshot captured (${r.snapshot_id.slice(0, 18)}…)`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Capture failed");
        } finally {
            setCapturing(false);
        }
    };

    const handleRestore = async () => {
        if (!restoreFor) return;
        if (confirmText.trim() !== "RESTORE") {
            toast.error("Type RESTORE to confirm");
            return;
        }
        setRestoring(true);
        try {
            const r = await api.siteRestoreDraftSnapshot(
                tournamentId, restoreFor.id, { confirm_token: "RESTORE" },
            );
            toast.success(`Restored — ${r.player_count} player(s) reassigned`);
            setRestoreFor(null);
            setConfirmText("");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Restore failed");
        } finally {
            setRestoring(false);
        }
    };

    const handleDelete = async (sid) => {
        if (!window.confirm("Delete this snapshot? This cannot be undone.")) return;
        setDeletingId(sid);
        try {
            await api.siteDeleteDraftSnapshot(tournamentId, sid);
            toast.success("Snapshot deleted");
            setSnapshots(curr => curr.filter(s => s.id !== sid));
            if (restoreFor?.id === sid) {
                setRestoreFor(null);
                setConfirmText("");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="snapshots-modal" className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Camera className="w-5 h-5 text-cyber" /> Draft snapshots
                    </DialogTitle>
                    <DialogDescription>
                        <span className="text-zinc-300">{tournamentName}</span>
                        <span className="block text-[11px] text-zinc-500 mt-1">
                            Snapshots auto-capture daily and right before any draft clear.
                            They include the draft document + each crewmate's drafted teams
                            for THIS tournament. Champion bonus picks, scoring, and matches
                            are NOT touched on restore. Snapshots auto-purge after 7 days
                            (or sooner if you delete them manually).
                        </span>
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center justify-between mt-2">
                    <button
                        type="button"
                        data-testid="capture-snapshot-btn"
                        onClick={handleManualCapture}
                        disabled={capturing}
                        className="brutal-btn text-xs py-1.5 px-3 bg-cyber text-black border-black"
                    >
                        <Camera className="w-3.5 h-3.5" />
                        {capturing ? "Capturing…" : "Capture now"}
                    </button>
                    <span className="text-[11px] text-zinc-500">
                        {loading ? "Loading…" : `${snapshots.length} snapshot${snapshots.length === 1 ? "" : "s"}`}
                    </span>
                </div>

                <div className="mt-3 max-h-[55dvh] overflow-y-auto space-y-2" data-testid="snapshots-list">
                    {!loading && snapshots.length === 0 && (
                        <div className="brutal-card-light p-4 text-center text-xs text-zinc-500">
                            No snapshots yet. The daily auto-capture runs every 24h; you can
                            also force one now with the button above.
                        </div>
                    )}
                    {snapshots.map(s => {
                        const isExpanded = restoreFor?.id === s.id;
                        return (
                            <div key={s.id} data-testid={`snapshot-row-${s.id}`} className="brutal-card-light p-3">
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-bold flex items-center gap-2">
                                            <Clock className="w-3.5 h-3.5 text-zinc-500" />
                                            {(s.taken_at_iso || s.taken_at || "").replace("T", " ").slice(0, 19)} UTC
                                        </p>
                                        <p className="text-[11px] text-zinc-400 mt-0.5">
                                            <span className="pill bg-ink border-[#333] text-zinc-300 mr-2">{s.reason}</span>
                                            {s.drafted_player_count} player{s.drafted_player_count === 1 ? "" : "s"} drafted
                                            {s.taken_by_email && (
                                                <> · by <span className="text-cyber">{s.taken_by_email}</span></>
                                            )}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        data-testid={`restore-snapshot-${s.id}-btn`}
                                        onClick={() => { setRestoreFor(s); setConfirmText(""); }}
                                        className="brutal-btn-ghost text-xs py-1.5 px-3"
                                    >
                                        <RotateCcw className="w-3.5 h-3.5 text-blaze" /> Restore
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`delete-snapshot-${s.id}-btn`}
                                        onClick={() => handleDelete(s.id)}
                                        disabled={deletingId === s.id}
                                        title="Delete this snapshot"
                                        className="brutal-btn-ghost text-xs py-1.5 px-2"
                                    >
                                        <Trash2 className="w-3.5 h-3.5 text-blaze" />
                                        {deletingId === s.id ? "…" : ""}
                                    </button>
                                </div>

                                {isExpanded && (
                                    <div className="mt-3 border-t border-[#333] pt-3 space-y-2" data-testid={`restore-confirm-${s.id}`}>
                                        <div className="flex items-start gap-2 text-[11px] text-blaze">
                                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                            <p>
                                                This OVERWRITES the current draft + each crewmate's drafted teams for
                                                this tournament. Champion bonus picks, scoring rules, and recorded
                                                matches are NOT touched. Type <strong>RESTORE</strong> to confirm.
                                            </p>
                                        </div>
                                        <input
                                            data-testid={`restore-confirm-input-${s.id}`}
                                            type="text"
                                            value={confirmText}
                                            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                                            placeholder="Type RESTORE"
                                            className="brutal-input w-full text-sm"
                                            autoFocus
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => { setRestoreFor(null); setConfirmText(""); }}
                                                className="brutal-btn-ghost text-xs flex-1"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                data-testid={`confirm-restore-${s.id}-btn`}
                                                onClick={handleRestore}
                                                disabled={restoring || confirmText.trim() !== "RESTORE"}
                                                className="brutal-btn-primary text-xs flex-1 bg-blaze text-black border-black disabled:opacity-40"
                                            >
                                                <RotateCcw className="w-3.5 h-3.5" />
                                                {restoring ? "Restoring…" : "Restore this snapshot"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <DialogFooter>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="snapshots-close-btn"
                        className="brutal-btn-ghost text-xs"
                    >
                        <X className="w-3.5 h-3.5" /> Close
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
